// =============================================================================
// Thin fetch wrapper with auto-attach of JWT and 401-driven refresh.
//
// Behavior on 401:
//   1. Attempt POST /auth/refresh with the stored refreshToken.
//   2. On success: store the new pair and retry the original request once.
//   3. On failure: clear the session and dispatch 'netrunner:session-expired'
//      so the router/guard can react without a hard import cycle.
//
// All requests go through `request<T>` (except login/register/logout/refresh,
// which are naked to avoid recursive refresh attempts).
// =============================================================================

import type { AuthResult } from '@/types/api';

const BASE = import.meta.env.VITE_API_URL ?? '/api';
const ACCESS_KEY = 'netrunner:token';
const REFRESH_KEY = 'netrunner:refresh';

export class ApiError extends Error {
  public readonly status: number;
  public readonly code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

/* -------------------------------------------------------------------------- */
/* Token storage                                                              */
/* -------------------------------------------------------------------------- */

export function getToken(): string | null { return localStorage.getItem(ACCESS_KEY); }
export function getRefreshToken(): string | null { return localStorage.getItem(REFRESH_KEY); }

export function setTokens(access: string | null, refresh: string | null): void {
  if (access === null) localStorage.removeItem(ACCESS_KEY);
  else localStorage.setItem(ACCESS_KEY, access);
  if (refresh === null) localStorage.removeItem(REFRESH_KEY);
  else localStorage.setItem(REFRESH_KEY, refresh);
}

// Back-compat: older code calls setToken(token) expecting only access.
export function setToken(token: string | null): void {
  setTokens(token, null);
}

/* -------------------------------------------------------------------------- */
/* Refresh coordination                                                       */
/* -------------------------------------------------------------------------- */

// Single in-flight refresh promise — many parallel 401s share one refresh.
let inflightRefresh: Promise<string | null> | null = null;
let sessionCleared = false;

function clearSession(): void {
  if (sessionCleared) return;
  sessionCleared = true;
  setTokens(null, null);
  window.dispatchEvent(new CustomEvent('netrunner:session-expired'));
  setTimeout(() => { sessionCleared = false; }, 0);
}

async function performRefresh(): Promise<string | null> {
  const refresh = getRefreshToken();
  if (!refresh) return null;
  try {
    const res = await fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: refresh }),
    });
    if (!res.ok) {
      clearSession();
      return null;
    }
    const pair = (await res.json()) as { token: string; refreshToken: string };
    setTokens(pair.token, pair.refreshToken);
    return pair.token;
  } catch {
    clearSession();
    return null;
  }
}

async function refreshAccessToken(): Promise<string | null> {
  if (!inflightRefresh) {
    inflightRefresh = performRefresh().finally(() => { inflightRefresh = null; });
  }
  return inflightRefresh;
}

/* -------------------------------------------------------------------------- */
/* Core request with auto-retry on 401                                        */
/* -------------------------------------------------------------------------- */

async function rawRequest(
  method: string,
  path: string,
  body?: unknown,
  withAuth = true,
): Promise<Response> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (withAuth) {
    const token = getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }
  return fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res = await rawRequest(method, path, body, true);

  if (res.status === 401 && getRefreshToken()) {
    const newAccess = await refreshAccessToken();
    if (newAccess) {
      res = await rawRequest(method, path, body, true);
    }
  }

  if (!res.ok) {
    let message = res.statusText;
    let code: string | undefined;
    try {
      const data = (await res.json()) as { error?: string; code?: string };
      if (data?.error) message = data.error;
      if (data?.code) code = data.code;
    } catch { /* ignore */ }
    throw new ApiError(message, res.status, code);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get:    <T>(p: string)              => request<T>('GET', p),
  post:   <T>(p: string, b?: unknown) => request<T>('POST', p, b),
  put:    <T>(p: string, b?: unknown) => request<T>('PUT', p, b),
  delete: <T>(p: string)              => request<T>('DELETE', p),
};

async function toApiError(res: Response): Promise<ApiError> {
  let message = res.statusText;
  try {
    const d = (await res.json()) as { error?: string };
    if (d?.error) message = d.error;
  } catch { /* */ }
  return new ApiError(message, res.status);
}

export const auth = {
  register: async (email: string, password: string, handle: string) => {
    const r = await rawRequest('POST', '/auth/register', { email, password, handle }, false);
    if (!r.ok) throw await toApiError(r);
    const data = (await r.json()) as AuthResult;
    setTokens(data.token, data.refreshToken);
    return data;
  },
  login: async (email: string, password: string) => {
    const r = await rawRequest('POST', '/auth/login', { email, password }, false);
    if (!r.ok) throw await toApiError(r);
    const data = (await r.json()) as AuthResult;
    setTokens(data.token, data.refreshToken);
    return data;
  },
  logout: async () => {
    const refresh = getRefreshToken();
    try {
      await rawRequest('POST', '/auth/logout', { refreshToken: refresh }, false);
    } catch { /* ignore */ }
    setTokens(null, null);
  },
};
